import { ampli, type SuggestedTokenSelectedProperties } from '@/ampli';
import { useMemo } from 'react';

import { AvatarSkeleton } from '@/components/Skeleton';

import {
  MAIN_USD_STABLE_ADDRESSES,
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID,
  USDC_ADDRESSES,
  EXCLUDED_SUGGESTED_ADDRESSES,
} from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import { useTokenResults } from '@/hooks/tokenQueries/useTokenResults';
import { useRecentlyTradedTokens } from '@/hooks/useRecentlyTradedTokens';
import { useWalletBalances } from '@/hooks/useWalletBalances';

import { fontStyles } from '@/styles/typography';

import { TokenBase, TokenResult } from '@/utils/0x/token-registry.types';
import { ChainAddress } from '@/utils/models';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import type { TokenBalance } from '@/utils/zerion/types';

import { TokenButton } from './tokenButton';
import { MIN_BALANCE_USD_FOR_SUGGESTION, NUMBER_OF_SUGGESTED_TOKENS } from './utils';

const getOtherSuggestedTokensAddresses = (
  chainId: number,
  recentlyTradedTokens: TokenBase[],
  tokenBalances: TokenBalance[],
  excludedAddresses: Set<ChainAddress>,
): ChainAddress[] => {
  // If there are token balances, return them (filter out excluded tokens and low-value balances)
  const tokenBalancesSorted = tokenBalances
    .filter(
      (item) =>
        !excludedAddresses.has(item.tokenAddress as ChainAddress) &&
        item.balanceUsd &&
        item.balanceUsd >= MIN_BALANCE_USD_FOR_SUGGESTION,
    )
    .map((item) => item.tokenAddress as ChainAddress);
  if (tokenBalancesSorted.length) return tokenBalancesSorted;

  // If there are recently traded tokens, return them
  const filteredRecentlyTradedTokenAddresses = recentlyTradedTokens
    .filter((token) => token.chainId === chainId && !excludedAddresses.has(token.address))
    .map((token) => token.address);
  if (filteredRecentlyTradedTokenAddresses.length) return filteredRecentlyTradedTokenAddresses;

  // Nothing to suggest, so default to USDC
  return [USDC_ADDRESSES[chainId]].filter(
    (tokenAddress) => !!tokenAddress && !excludedAddresses.has(tokenAddress),
  );
};

const getTokenAddresses = (
  chainId: number,
  counterpartTokenAddress: ChainAddress,
  isWalletConnected: boolean,
  recentlyTradedTokens: TokenBase[],
  tokenBalances: TokenBalance[],
): ChainAddress[] => {
  const nativeTokenAddress = NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId];
  const mainStableTokenAddress = MAIN_USD_STABLE_ADDRESSES[chainId];
  const excludedTokenAddresses = EXCLUDED_SUGGESTED_ADDRESSES[chainId] ?? [];

  // If the wallet is not connected
  if (!isWalletConnected) {
    return [nativeTokenAddress, USDC_ADDRESSES[chainId], mainStableTokenAddress].filter(
      (tokenAddress) =>
        !!tokenAddress &&
        tokenAddress !== counterpartTokenAddress &&
        !excludedTokenAddresses.includes(tokenAddress),
    );
  }

  // If the wallet is connected
  const otherTokenAddresses = getOtherSuggestedTokensAddresses(
    chainId,
    recentlyTradedTokens,
    tokenBalances,
    new Set([nativeTokenAddress, mainStableTokenAddress, counterpartTokenAddress]),
  );

  // Compose the final list of suggested tokens
  const finalTokenAddresses = [nativeTokenAddress, mainStableTokenAddress, ...otherTokenAddresses]
    .filter(
      (tokenAddress) =>
        !!tokenAddress &&
        tokenAddress !== counterpartTokenAddress &&
        !excludedTokenAddresses.includes(tokenAddress),
    )
    .slice(0, NUMBER_OF_SUGGESTED_TOKENS);

  return finalTokenAddresses;
};

export const InlineSuggestedTokens = ({
  accountAddress,
  chainId,
  counterpartTokenAddress,
  onSelect,
  orderType,
  ownedTokens,
}: {
  /** Wallet for the empty-side balance ranking; not needed in owned mode. */
  accountAddress?: ChainAddress;
  chainId: number;
  counterpartTokenAddress: ChainAddress;
  onSelect(selectedToken: TokenResult): void;
  orderType: SuggestedTokenSelectedProperties['swap_order_type'];
  /** Pre-resolved held-token suggestions; presence switches to owned mode. */
  ownedTokens?: TokenResult[];
}) => {
  const isOwnedOnly = ownedTokens != null;
  const { tokens: recentlyTradedTokens } = useRecentlyTradedTokens({
    chainId,
    enabled: !isOwnedOnly && !!accountAddress,
  });

  const { data: balancesData } = useWalletBalances({
    evmAddress: chainId === CHAIN_IDS.SOLANA ? undefined : accountAddress,
    solanaAddress: chainId === CHAIN_IDS.SOLANA ? accountAddress : undefined,
    chainIds: [chainId],
  });

  // Owned suggestions arrive pre-resolved, so the empty-side path resolves
  // nothing in that mode.
  const tokenAddresses = useMemo(
    () =>
      isOwnedOnly
        ? []
        : getTokenAddresses(
            chainId,
            counterpartTokenAddress,
            !!accountAddress,
            recentlyTradedTokens,
            balancesData || [],
          ),
    [
      isOwnedOnly,
      chainId,
      counterpartTokenAddress,
      accountAddress,
      recentlyTradedTokens,
      balancesData,
    ],
  );

  const { tokenMap, isLoading } = useTokenResults(tokenAddresses, chainId);

  const chainTokens = tokenMap?.[chainId];
  // Preserve the ranked order so the chips and analytics rank match the
  // builder, deduping on the normalized address - the builder's sources can
  // overlap (e.g. USDC is also the main stable on most chains). Exclude the
  // counterpart on the normalized address too: its upstream `!==` checks are
  // case-sensitive, so a checksummed balance/registry address could otherwise
  // slip the counterpart in and let the same token sit on both sides.
  const normalizedCounterpart = normalizeTokenAddress(counterpartTokenAddress, chainId);
  const resolvedTokens = chainTokens
    ? Array.from(new Set(tokenAddresses.map((address) => normalizeTokenAddress(address, chainId))))
        .filter((address) => address !== normalizedCounterpart)
        .map((address) => chainTokens[address])
        .filter((token): token is TokenResult => !!token)
    : [];
  const tokens = ownedTokens ?? resolvedTokens;

  const handleSelect = (token: TokenResult, rank: number) => {
    ampli.suggestedTokenSelected({
      chain_id: chainId,
      rank,
      suggestion_mode: isOwnedOnly ? 'owned_only' : 'empty_side',
      swap_order_type: orderType,
      token_symbol: token.symbol,
    });
    onSelect(token);
  };

  // Show skeleton buttons while loading
  if (isLoading && tokens.length === 0) {
    return (
      <div
        className={fontStyles({
          variant: 'BODY_SM',
          class: 'flex flex-row gap-2 -mr-2 text-primitive-gray-40 items-center',
        })}
      >
        {Array.from({ length: NUMBER_OF_SUGGESTED_TOKENS }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-token-gray-200"
          >
            <AvatarSkeleton size={16} />
            <div className="w-8 h-3 rounded-xs bg-token-gray-400 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (tokens.length === 0) return null;

  return (
    <div
      className={fontStyles({
        variant: 'BODY_SM',
        class: 'flex flex-row gap-2 -mr-2 text-primitive-gray-40 items-center',
      })}
    >
      {tokens.map((token, index) => (
        <TokenButton
          key={token.address}
          token={token}
          onClick={() => handleSelect(token, index + 1)}
        />
      ))}
    </div>
  );
};
